import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Loading, StatusBadge, PriorityBadge, SourceBadge, EmptyState, PageHeader } from "@/components/common";
import { FileText, Filter, X } from "lucide-react";

export default function Bills() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);

  const get = (k) => params.get(k) || "";
  const setF = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => {
    setData(null);
    const qs = params.toString();
    api.get(`/bills?${qs}`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const filters = [
    ["q", "Search bills…", null],
    ["status", "Status", meta?.statuses?.map((s) => [s, s])],
    ["chamber", "Chamber", meta?.chambers?.map((s) => [s, s])],
    ["priority", "Priority", meta?.priorities?.map((s) => [s, s])],
    ["policy_area_id", "Policy Area", meta?.policy_areas?.map((p) => [p.policy_area_id, p.name])],
    ["session_id", "Session", meta?.sessions?.map((s) => [s.session_id, s.name])],
    ["source_type", "Source", [["sample", "Sample"], ["live", "Live"]]],
  ];
  const active = [...params.keys()].filter((k) => !["skip", "limit"].includes(k));
  const selectCls = "h-9 rounded-lg bg-white border border-brand-line px-2 text-sm text-brand-ink outline-none focus:border-brand-civic transition-colors";

  return (
    <div>
      <PageHeader title="Bills" subtitle={data ? `${data.total} bill${data.total === 1 ? "" : "s"} matching your filters` : "Legislative bill records"} />

      <div className="rounded-xl border border-brand-line bg-white p-4 mb-4 shadow-card">
        <div className="flex items-center gap-2 mb-3 text-brand-navy text-sm font-semibold"><Filter className="h-4 w-4 text-brand-civic" /> Filters</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          <input data-testid="bills-search-input" value={get("q")} onChange={(e) => setF("q", e.target.value)} placeholder="Search bills…"
            className="col-span-2 lg:col-span-1 h-9 rounded-lg bg-white border border-brand-line px-3 text-sm text-brand-ink placeholder:text-slate-400 outline-none focus:border-brand-civic transition-colors" />
          {filters.slice(1).map(([k, label, opts]) => (
            <select key={k} data-testid={`filter-${k}`} value={get(k)} onChange={(e) => setF(k, e.target.value)} className={selectCls}>
              <option value="">{label}: All</option>
              {(opts || []).map(([val, txt]) => <option key={val} value={val}>{txt}</option>)}
            </select>
          ))}
        </div>
        {active.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {active.map((k) => (
              <button key={k} onClick={() => setF(k, "")} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-brand-civic-50 text-brand-navy border border-brand-civic/30 hover:bg-brand-civic-100" data-testid={`active-filter-${k}`}>
                {k}: {get(k)} <X className="h-3 w-3" />
              </button>
            ))}
            <button onClick={() => setParams(new URLSearchParams())} className="text-xs font-medium text-slate-500 hover:text-brand-navy" data-testid="clear-filters">Clear all</button>
          </div>
        )}
      </div>

      {data === null ? <Loading /> : !data || data.bills.length === 0 ? (
        <EmptyState icon={FileText} title="No bills found" description="Try adjusting your filters." />
      ) : (
        <div className="rounded-xl border border-brand-line overflow-hidden bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bills-table">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-brand-line">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Bill</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Title</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Priority</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Policy Area</th>
                  <th className="text-left px-4 py-3 font-semibold">Source</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-line">
                {data.bills.map((b) => (
                  <tr key={b.bill_id} className="hover:bg-slate-50 transition-colors" data-testid={`bill-row-${b.bill_number.replace(/\s/g, "")}`}>
                    <td className="px-4 py-3"><Link to={`/bills/${b.bill_id}`} className="font-mono font-semibold text-brand-civic hover:text-brand-navy hover:underline">{b.bill_number}</Link><div className="text-[11px] text-slate-400">{b.chamber}</div></td>
                    <td className="px-4 py-3 hidden md:table-cell"><Link to={`/bills/${b.bill_id}`} className="text-slate-700 hover:text-brand-navy">{b.title}</Link></td>
                    <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
                    <td className="px-4 py-3 hidden lg:table-cell"><PriorityBadge priority={b.priority} /></td>
                    <td className="px-4 py-3 hidden lg:table-cell text-slate-500">{b.policy_area || "—"}</td>
                    <td className="px-4 py-3"><SourceBadge type={b.source_type} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
